import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { normalizePatientId } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";

/**
 * Memória só do GET unitário de fallback.
 * A lista da organização continua em `org-patients:` e não é gravada aqui.
 */
const INSIGHT_PATIENT_KEY_PREFIX = "clinical-insights:patient:";

export const PATIENT_FETCH_CONCURRENCY = 8;

export function insightPatientCacheKey(patientId: string): string {
  return `${INSIGHT_PATIENT_KEY_PREFIX}${normalizePatientId(patientId)}`;
}

/**
 * Índice da lista de atendidos. Consentimento entra só para id que a lista
 * de atendidos não tem — e só se essa lista já estiver em memória.
 * Person não é Patient e não entra aqui.
 */
export function indexOrganizationPatients(
  attendedEntries: readonly fhir5.BundleEntry<fhir5.Patient>[] | undefined,
  consentPatients: readonly fhir5.Patient[] | undefined,
): Map<string, fhir5.Patient> {
  const map = new Map<string, fhir5.Patient>();

  for (const entry of attendedEntries ?? []) {
    const patient = entry.resource;
    const id = patient?.id ? normalizePatientId(patient.id) : "";
    if (!id || !patient) continue;
    map.set(id, patient);
  }

  for (const patient of consentPatients ?? []) {
    const id = patient?.id ? normalizePatientId(patient.id) : "";
    if (!id || map.has(id)) continue;
    map.set(id, patient);
  }

  return map;
}

async function fetchPatientResource(patientId: string): Promise<fhir5.Patient> {
  const url = `${endpoints.MPIFHIR}/Patient/${patientId}`;
  const headers = {
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    Accept: "application/fhir+json",
  };
  const response = await useAxios.get(url, { headers, timeout: 20000 });
  if (response.status === HTTPResponseStatus.Success && response.data) {
    return response.data as fhir5.Patient;
  }
  throw new Error(`Paciente ${patientId} indisponível`);
}

export interface GetOrFetchInsightPatientOptions {
  force?: boolean;
  organizationPatients?: ReadonlyMap<string, fhir5.Patient>;
}

/** Lista da organização primeiro. O GET unitário só roda para quem não está nela. */
export function getOrFetchInsightPatient(
  patientId: string,
  options?: GetOrFetchInsightPatientOptions,
): Promise<fhir5.Patient | null> {
  const id = normalizePatientId(patientId);
  if (!id || id === "unknown") return Promise.resolve(null);

  const fromOrganization = options?.organizationPatients?.get(id);
  if (fromOrganization) return Promise.resolve(fromOrganization);

  return sharedMemoryCache
    .getOrFetch(insightPatientCacheKey(id), () => fetchPatientResource(id), {
      force: options?.force,
    })
    .catch((err: unknown) => {
      console.error(`Erro ao buscar paciente ${id}:`, err);
      return null;
    });
}

export interface GetOrFetchInsightPatientsOptions {
  concurrency?: number;
  shouldContinue?: () => boolean;
  onProgress?: (loaded: number, total: number) => void;
  force?: boolean;
  /** Pacientes já resolvidos pela cache da organização. Não dispara essa busca. */
  organizationPatients?: ReadonlyMap<string, fhir5.Patient>;
}

/** Resolve vários pacientes. Quem já está na lista da organização não gera GET. */
export async function getOrFetchInsightPatients(
  patientIds: string[],
  options?: GetOrFetchInsightPatientsOptions,
): Promise<Map<string, fhir5.Patient>> {
  const concurrency = options?.concurrency ?? PATIENT_FETCH_CONCURRENCY;
  const organizationPatients = options?.organizationPatients;
  const uniqueIds = Array.from(
    new Set(
      patientIds.map((id) => normalizePatientId(id)).filter((id) => id && id !== "unknown"),
    ),
  );
  const patientMap = new Map<string, fhir5.Patient>();
  const missing: string[] = [];

  for (const id of uniqueIds) {
    const fromOrganization = organizationPatients?.get(id);
    if (fromOrganization) {
      patientMap.set(id, fromOrganization);
      continue;
    }
    if (options?.force) {
      missing.push(id);
      continue;
    }
    const cached = sharedMemoryCache.get<fhir5.Patient>(insightPatientCacheKey(id));
    if (cached) patientMap.set(id, cached.data);
    else missing.push(id);
  }

  const total = uniqueIds.length;
  let completed = total - missing.length;
  options?.onProgress?.(completed, total);
  if (missing.length === 0) return patientMap;

  for (let i = 0; i < missing.length; i += concurrency) {
    if (options?.shouldContinue && !options.shouldContinue()) return patientMap;

    const batch = missing.slice(i, i + concurrency);
    const results = await Promise.all(
      batch.map(async (id) => {
        const patient = await getOrFetchInsightPatient(id, {
          force: options?.force,
          organizationPatients,
        });
        return patient ? ([id, patient] as const) : null;
      }),
    );

    for (const result of results) {
      if (result) patientMap.set(result[0], result[1]);
    }
    completed += batch.length;
    options?.onProgress?.(Math.min(completed, total), total);
  }

  return patientMap;
}
